import {
  Directive,
  OnInit,
  Input,
  ElementRef,
  NgZone,
  OnChanges,
  SimpleChanges,
  inject,
  DestroyRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, ReplaySubject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  switchMap,
  tap
} from 'rxjs/operators';

import { environment } from '@env';
import { SettingsState } from '@core/settings';

import { formatNumber } from '../common/format-number';
import {
  isNumber,
  isString,
  isValue,
  strToNumber
} from '../helpers';
import { BrowserApiService } from '../common';

import { INTERNAL_CURRENCY_CODE } from './constants';
import { Converter } from './converter.service';
import { Configuration } from './convert-directive.configuration';
import { CurrenciesState } from './currencies.state';
import { animatedRender } from './utils';

@Directive({
  selector: '[convert]'
})
export class ConvertDirective implements OnInit, OnChanges {
  private readonly elementRef = inject(ElementRef);
  private readonly browser = inject(BrowserApiService);
  private readonly converter = inject(Converter);
  private readonly ngZone = inject(NgZone);
  private readonly currenciesState = inject(CurrenciesState);
  private readonly destroyRef = inject(DestroyRef);
  private readonly settingsState = inject(SettingsState);

  @Input('convert')
  public value!: any;

  @Input()
  public convertFrom!: string;

  @Input()
  public convertTo!: string;

  @Input()
  public convertDigits?: string; // {minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}

  @Input()
  public convertClassName = 'currency';

  @Input()
  public convertAnimation = false;

  @Input()
  public duration: number|string = Configuration.DEFAULT_ANIMATION_DURATION;

  private readonly convertedValue$ = new ReplaySubject<number>(1);
  private readonly isBrowser = this.browser.isBrowser();

  private isFirstChange = true;
  private lastValue?: number;
  private prevClassName?: string;
  private validConvertTo?: string;
  private digitsInfo?: string;

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.setupListener();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.ngZone.runOutsideAngular(() => {
      if(changes['convertTo'] || !this.validConvertTo) {
        this.setConvertTo();
      }

      if(changes['digitsInfo'] || !this.digitsInfo) {
        this.setDigitsInfo();
      }

      if(changes['value'] || changes['convertFrom'] || changes['convertTo']) {
        const convertedValue = this.convert(this.value, this.convertFrom, this.validConvertTo);

        if(null !== convertedValue) {
          this.convertedValue$.next(strToNumber(convertedValue));
        }
      }
    });
  }

  private setupListener(): void {
    this.convertedValue$
      .pipe(
        switchMap(endValue => {
          const animate = !this.isFirstChange &&
            this.convertAnimation &&
            Number(this.duration) > 0 &&
            !this.settingsState.disableAnimations();

          this.isFirstChange = false;

          this.setClassName();

          if(animate && this.isBrowser) {
            const startValue = this.lastValue || endValue;
            const duration = Number(this.duration);

            return animatedRender(endValue, startValue, duration).pipe(
              map(value => {
                this.lastValue = value;

                return formatNumber(value, this.digitsInfo);
              }),
              distinctUntilChanged()
            );
          }

          return of(formatNumber(endValue, this.digitsInfo));
        }),
        tap(currentValue => this.renderValue(currentValue)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private setConvertTo(): void {
    const isStreamerMode = this.settingsState.streamerMode();
    const userCurrency = this.currenciesState.userCurrency();

    if(isStreamerMode && userCurrency === environment.baseCurrency) {
      this.validConvertTo = INTERNAL_CURRENCY_CODE;
    } else {
      this.validConvertTo = this.convertTo || this.currenciesState.userCurrency();
    }
  }

  private setDigitsInfo(): void {
    let digitsInfo = this.convertDigits;

    if(undefined === digitsInfo) {
      const currencyDigitsInfo = this.validConvertTo ? Configuration.DIGITS_INFO[this.validConvertTo] : undefined;

      digitsInfo = currencyDigitsInfo || Configuration.DEFAULT_DIGITS_INFO;
    }

    this.digitsInfo = digitsInfo;
  }

  private convert(value: any, convertFrom?: string, convertTo?: string): string|null {
    if(!isNumber(value) && !isString(value) || Number.isNaN(value)) {
      return null;
    }

    if(convertTo === INTERNAL_CURRENCY_CODE) {
      convertTo = environment.baseCurrency;
    }

    let result: string|null;

    if(convertFrom !== convertTo) {
      result = this.converter.convert(
        Number(value),
        convertFrom,
        convertTo,
        null
      );
    } else {
      result = String(value);
    }

    if('NaN' === result) {
      return null;
    }

    return result;
  }

  private renderValue(value: string): void {
    const { nativeElement } = this.elementRef;

    if(nativeElement instanceof HTMLInputElement) {
      const symbol = Configuration.SYMBOLS[this.validConvertTo!];

      if('RUB' === this.validConvertTo) {
        nativeElement.value = `${ value } ${ symbol }`;
      } else {
        nativeElement.value = `${ symbol } ${ value }`;
      }
    } else {
      nativeElement.textContent = value;
    }
  }

  private setClassName(): void {
    const { nativeElement } = this.elementRef;

    if(nativeElement instanceof HTMLInputElement) {
      return;
    }

    const className = `${ this.convertClassName }_${ this.validConvertTo }`;

    if(!nativeElement.classList.contains(className)) {
      nativeElement.classList.add(className);
    }

    if(this.prevClassName !== className) {
      if(isValue(this.prevClassName)) {
        nativeElement.classList.remove(this.prevClassName);
      }

      this.prevClassName = className;
    }
  }
}
